import { BROWSE_INVENTORY } from '@makemydeal/activities-tradein';
import type { AdditionalSelectors } from '@makemydeal/activities-tradein';
import type { IAdditionalSelectors } from '@makemydeal/dr-activities-tradein';
import type { MessageBox } from '@makemydeal/dr-platform-types';

// Selectors
import {
    getCreditProvider,
    getDealerName,
    getDealerPhone,
    getDealerState,
    getDealerDetails,
    getDealerZip,
    getDealVisibility,
    getTradeInHideExcellentConditionListingSite,
    getTradeInHideExcellentConditionWebsite,
    getTradeInSource,
    getTradeInValuationProvider,
    getVehicleDetails,
    getVehicleImageUrl,
    getVehicleVin,
    hasTradeIn,
    isCreditAppCompleted,
    isSubaruGtpEnabled
} from '@makemydeal/dr-sales-checkin-store';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as offerSelectors from '../../selectors/offerSelectors';
import * as routeSelectors from '../../selectors/routeSelectors';

// Utilities
import { formatUtils } from '@makemydeal/dr-common-utils';
import { shortDateString } from '../../utils/dateFormatters';

const { formatDollars, formatDollarsAndCents } = formatUtils;

export const additionalTradeSelectors: IAdditionalSelectors = {
    dealerSelectors: {
        isTradeValuationKbb: () => true,
        getDealerTradeInValuationProvider: getTradeInValuationProvider,
        isDealerTradeInLeadFormForWebsiteEnabled: () => true,
        isDealerTradeInLeadFormForListingSiteEnabled: () => true,
        getDealerState: getDealerState,
        testDriveAtHomeOnTradeIn: () => false,
        isVppEnabled: () => false,
        getCreditProvider: getCreditProvider,
        getDealerPhone: getDealerPhone,
        getDealerName: getDealerName,
        hasNoTradeOwnership: () => false,
        isSubaruGtpEnabled: isSubaruGtpEnabled,
        getTradeInHideExcellentConditionWebsite: getTradeInHideExcellentConditionWebsite,
        getTradeInHideExcellentConditionListingSite: getTradeInHideExcellentConditionListingSite,
        isGhostVinDealerEnabled: () => false
    },

    configSelectors: {
        isP202RedesignEnabled: () => true,
        isActivityEnabled: () => true,
        isGhostVinEnabled: () => false,
        isAmdLienPayoffQuoteSpOnSP: (state) => featureToggleSelectors.isTradeInAutoPayoffEnabledSCI(state)
    },

    connectionSelectors: {
        getConnectionId: () => ''
    },

    offerSelectors: {
        getHasBeenSent: offerSelectors.getHasBeenSent,
        getIsOfferBeingSubmitted: offerSelectors.getIsOfferBeingSubmitted,
        getMonthlyPaymentRoundedByOfferType: offerSelectors.getMonthlyPaymentRoundedByOfferType,
        getCurrentOfferType: offerSelectors.getCurrentOfferType,
        getShopperInfo: offerSelectors.getShopperInfo,
        getShopperZip: (state) => offerSelectors.getShopperZip(state) || getDealerZip(state),
        isMonthlyPaymentInvalid: () => false,
        getOfferCreationDateString: offerSelectors.getOfferCreationDateString,
        getVehicleDetails: getVehicleDetails,
        getAnalyticsPayloadVehicleDetails: getVehicleDetails,
        isPaymentInvalid: () => false,
        createGatewayOfferPayload: offerSelectors.createGatewayOfferPayload,
        createOfferPayload: offerSelectors.createOfferPayload,
        isOfferFromDealer: () => false,
        getOfferDealerSource: offerSelectors.getOfferDealerSource
    },

    creditSelectors: {
        // TODO: Understand how tradein is related tp credit app and implement properly
        isCreditAppCompleted: isCreditAppCompleted
    },

    dateFormatters: {
        shortDateString
    },

    formatUtils: {
        formatDollars,
        formatDollarsAndCents
    },

    vehicleSelectors: {
        getVehicleImageUrl: getVehicleImageUrl,
        getVehicleVin: getVehicleVin
    },

    routeSelectors: {
        getCurrentLocation: routeSelectors.getCurrentLocation,
        isUserRedirectedToManualEntry: routeSelectors.isUserRedirectedToManualEntry,
        isDDPPage: routeSelectors.isDDPPage,
        isDealerShowroomExperience: routeSelectors.isDealerShowroomExperience
    },

    widgetSelectors: {
        getSponsor: () => 'atc',
        isDealerUser: () => false
    },

    uiHelpersUtil: {
        milesToStringShort: (miles: number | string) => miles.toString()
    },

    leadFormInfoSelectors: {
        isOfferSubmitSuccess: () => true,
        isOfferSubmitFailed: () => false
    },

    leadForm: {
        onOpenLeadFormModal: () => ({ type: 'FAKE_OPEN_LEAD_FORM' }),
        onFormSubmit: () => ({}),
        isLeadFormSubmittedSuccess: () => true,
        renderLeadForm: () => null
    },

    navigationCardSelectors: {
        getNextCardDetails: () => ({}),
        getNextStepInfo: () => ({ title: BROWSE_INVENTORY, route: '/inventory' }),
        getPrevStepInfo: () => ({ route: '/inventory' })
    },

    menu: {
        getMenuRoutes: () => ({}),
        menuSelectors: () => ({})
    },

    activitiesStatusSelectors: {
        hideNextActivityFooter: () => false,
        isTestDriveServiceUnavailable: () => false
    },

    paymentSelectors: {
        isCalculatingPayment: () => false
    },

    editPaymentActivitiesSelectors: {
        messageBox: () => null as unknown as MessageBox
    },

    tradeInSelectors: {
        getTradeInSource: getTradeInSource
    }
};

export const additionalSCISelectors: AdditionalSelectors = {
    hasTradeIn,
    useDealerDetailsSelector: getDealerDetails,
    getDealVisibility
};
